import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../axiosInstance";

const API_BASE_URL = "/api/v1/buyer-seller-connections";

// Create Buyer-Seller Connection
export const createBuyerSellerConnection = createAsyncThunk(
  "buyerNetwork/createBuyerSellerConnection",
  async ({ buyerEmail, buyerPhone, buyerCategory }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_BASE_URL}/connection`,
        { buyerEmail, buyerPhone, buyerCategory },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to create buyer-seller connection",
      );
    }
  },
);

// Fetch Buyer Connections
export const fetchBuyerConnections = createAsyncThunk(
  "buyerNetwork/fetchBuyerConnections",
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      // API call with query strings
      const response = await axios.get(
        `${API_BASE_URL}/connection?page=${page}&limit=${limit}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const { data, totalPages, totalConnections } = response.data;
      const formattedConnections = data.map((conn) => ({
        _id: conn._id,
        initials: getInitials(conn.otherUser?.name || "BU"),
        name: conn.otherUser?.name,
        email: conn.otherUser?.email || "-",
        phone: conn.otherUser?.phone || "-",
        phoneVerified: conn.otherUser?.phoneVerified ?? false,
        company: conn.otherUser?.businessName || "N/A",
        city: conn.otherUser?.businessAddress || "Jaipur",
        status: conn.status,
        buyerCategory: conn.buyerCategory?.name || "N/A",
        connectionData: conn,
        buyerId: conn.otherUser?._id,
        profileImage: conn.otherUser?.profileImage,
      }));

      return { formattedConnections, totalPages, totalConnections };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch",
      );
    }
  },
);

// Update Connection Status (Accept / Reject / Restore)
export const updateConnectionStatus = createAsyncThunk(
  "buyerNetwork/updateConnectionStatus",
  async ({ connectionId, status }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${API_BASE_URL}/connection/${connectionId}/status`,
        { status },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update connection status",
      );
    }
  },
);

// Assign / Update Buyer Category (Direct Accepted)
export const assignBuyerCategory = createAsyncThunk(
  "buyerNetwork/assignBuyerCategory",
  async ({ buyerEmail, buyerPhone, buyerCategory }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.post(
        `${API_BASE_URL}/assign/category`,
        { buyerEmail, buyerPhone, buyerCategory },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data.data; // connection object
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to assign buyer category",
      );
    }
  },
);

// Fetch View Members (BuyerCategory + Seller) with pagination
export const fetchViewMembers = createAsyncThunk(
  "buyerNetwork/fetchViewMembers",
  async (
    { buyerCategory, seller, page = 1, limit = 10 },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/view-members`, {
        buyerCategory,
        seller,
        page,
        limit,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch members",
      );
    }
  },
);

// Fetch Sellers List (Accepted Sellers)
export const fetchSellersList = createAsyncThunk(
  "buyerNetwork/fetchSellersList",
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/sellers-list?page=${page}&limit=${limit}`,
      );

      console.log(response, "api response");
      const {
        data,
        totalPages,
        totalSellers,
        currentPage,
      } = response.data;

      // format seller data
      const formattedSellers = data.map((conn) => ({
        _id: conn._id,
        sellerId: conn.seller?._id,
        name: conn.seller?.name || "Unknown",
        email: conn.seller?.email || "-",
        phone: conn.seller?.phone || "-",
        company: conn.seller?.businessName || "N/A",
        address: conn.seller?.businessAddress || "N/A",
        profileImage: conn.seller?.profileImage?.url || "",
        initials: getInitials(conn.seller?.name || "SE"),
        connectionData: conn,
      }));

      return {
        sellers: formattedSellers,
        totalPages,
        totalSellers,
        currentPage,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch sellers list"
      );
    }
  }
);

const initialState = {
  buyers: [],
  viewMembers: {},
  membersCount: 0,
  tabValue: 0,
  searchValue: "",
  loading: false,
  error: null,
  totalPages: 1,
  totalConnections: 0,
  sellers: [],
totalSellers: 0,
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
    nextPage: null,
    prevPage: null,
  },
};

const buyerNetworkSlice = createSlice({
  name: "buyerNetwork",
  initialState,
  reducers: {
    setTabValue(state, action) {
      state.tabValue = action.payload;
    },
    setSearchValue(state, action) {
      state.searchValue = action.payload;
    },
    addNewConnection(state, action) {
      const newConnection = action.payload;
      state.buyers.unshift({
        _id: newConnection._id,
        initials: getInitials(newConnection?.buyerEmail || "BU"),
        name:
          newConnection?.otherUser?.name ||
          newConnection?.buyerEmail ||
          "Unknown User",
        email: newConnection?.otherUser?.email || newConnection?.buyerEmail,
        phone: newConnection?.otherUser?.phone || newConnection?.buyerPhone,
        city: newConnection?.otherUser?.businessAddress || "Bangalore",
        status: newConnection.status || "Pending",
        profileImage: newConnection?.otherUser?.profileImage || "",
        connectionData: newConnection,
      });
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBuyerConnections.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBuyerConnections.fulfilled, (state, action) => {
        state.loading = false;
        state.buyers = action.payload.formattedConnections;
        state.totalPages = action.payload.totalPages; // Update total pages
        state.totalConnections = action.payload.totalConnections;
      })
      .addCase(fetchBuyerConnections.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create Buyer-Seller Connection
      .addCase(createBuyerSellerConnection.pending, (state) => {
        state.loading = true;
      })
      .addCase(createBuyerSellerConnection.fulfilled, (state) => {
        state.loading = false;
      })

      .addCase(createBuyerSellerConnection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Connection Status
      .addCase(updateConnectionStatus.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.buyers.findIndex((b) => b._id === updated._id);
        if (index !== -1) {
          state.buyers[index].status = updated.status;
        }
      })
      // Assign Buyer Category
      .addCase(assignBuyerCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(assignBuyerCategory.fulfilled, (state, action) => {
        state.loading = false;

        const updatedConn = action.payload;

        const index = state.buyers.findIndex((b) => b._id === updatedConn._id);

        if (index !== -1) {
          // Existing buyer UPDATE
          state.buyers[index] = {
            ...state.buyers[index],
            status: updatedConn.status, // Accepted
            buyerCategory: updatedConn.buyerCategory?.name || "N/A",
            connectionData: updatedConn,
          };
        } else {
          // New buyer ADD
          state.buyers.unshift({
            _id: updatedConn._id,
            initials: getInitials(updatedConn?.otherUser?.name || "BU"),
            name:
              updatedConn?.otherUser?.name ||
              updatedConn?.buyerEmail ||
              "Unknown User",
            email: updatedConn?.otherUser?.email || updatedConn?.buyerEmail,
            phone: updatedConn?.otherUser?.phone || updatedConn?.buyerPhone,
            city: updatedConn?.otherUser?.businessAddress || "Bangalore",
            status: updatedConn.status || "Accepted",
            buyerCategory: updatedConn.buyerCategory?.name || "N/A",
            profileImage: updatedConn?.otherUser?.profileImage || "",
            connectionData: updatedConn,
          });
        }
      })
      .addCase(assignBuyerCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchViewMembers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchViewMembers.fulfilled, (state, action) => {
        state.loading = false;

        const { data, count, pagination } = action.payload;

        // Store pagination data
        state.membersCount = count;
        state.pagination = pagination;

        // Format buyers data
        state.buyers = data.map((conn) => ({
          _id: conn._id,
          initials: getInitials(conn.buyer?.name || "BU"),
          name: conn.buyer?.name || "Unknown User",
          email: conn.buyer?.email || "-",
          phone: conn.buyer?.phone || conn.buyerPhone || "-",
          city: conn.buyer?.businessAddress || "N/A",
          status: conn.status,
          buyerCategory: conn.buyerCategory?.name || "N/A",
          connectionData: conn,
          buyerId: conn.buyer?._id,
          profileImage: conn.buyer?.profileImage,
        }));
      })
      .addCase(fetchViewMembers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Sellers List
.addCase(fetchSellersList.pending, (state) => {
  state.loading = true;
  state.error = null;
})
.addCase(fetchSellersList.fulfilled, (state, action) => {
  state.loading = false;
  state.sellers = action.payload.sellers;
  state.totalPages = action.payload.totalPages;
  state.totalSellers = action.payload.totalSellers;
})
.addCase(fetchSellersList.rejected, (state, action) => {
  state.loading = false;
  state.error = action.payload;
})
  },
});

// Helper function to get initials
const getInitials = (name) => {
  if (!name) return "BU";
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

export const { setTabValue, setSearchValue, addNewConnection, clearError } =
  buyerNetworkSlice.actions;
export default buyerNetworkSlice.reducer;
